// Geboortevenster · De betrouwbaarheidschip bij de geboortegegevens (Geavanceerd): drie gekleurde segmenten uit de schatting.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '../bodygraph/hd-bodygraph-render-state.factory';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { buildBirthWindowIntervals } from './birth-window-activations.builder';
import { formatBirthClock, formatBirthWindowDuration, hdBirthWindowTextKey } from './birth-window-format';
import {
  HD_BIRTH_WINDOW_OVERLAY_TIERS,
  HdBirthWindowOverlay,
  createHdBirthWindowOverlayEvaluator,
  filterHdBirthWindowOverlayLanes,
  toHdBirthWindowOverlayData,
} from './birth-window-moment';
import { HdBirthWindowTierMargin, marginsFromLanes, planetTierMargin, tierMargins } from './birth-window-margins';
import { buildBirthWindowLanes, createBirthWindowEvaluator } from './birth-window-properties.builder';
import { HD_BIRTH_WINDOW_INDICATOR_TIERS, HdBirthWindowTier } from './birth-window.models';
import { HdBirthWindowDataService } from './hd-birth-window-data.service';
import { HdBirthWindowLabelService } from './hd-birth-window-label.service';

const BW = 'humanDesign.chart.birthWindow';
/** Horizon van de schatting: ruim genoeg om voor de drie lagen een "stabiel tot" te vinden. */
const ESTIMATE_HALF_WINDOW_MS = 12 * 3_600_000;

interface IndicatorSegmentVm {
  readonly tier: HdBirthWindowTier;
  readonly label: string;
  readonly color: string;
  readonly critical: boolean;
  readonly text: string;
}

/**
 * Betrouwbaarheidschip bij de geboortegegevens (spec §3.1): drie segmenten
 * (Basics · Planeten · Variabelen) in de drempelkleur van hun marge,
 * berekend uit de eerste-orde-schatting (geen ephemeris-calls). Klik opent
 * het geboortevenster.
 */
@Component({
  selector: 'hd-birth-window-indicator',
  templateUrl: './hd-birth-window-indicator.component.html',
  styleUrl: './hd-birth-window-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdBirthWindowIndicatorComponent {
  readonly preset = input<HdBodygraphChartPreset | null | undefined>(null);
  /** Chart van een moment (Just Now, Rave New Year): tooltip over het tijdstip i.p.v. de geboorte. */
  readonly moment = input(false);
  /** Bewaard moment: de natal chart als vaste laag (enkel Basics en Planeten, zie `birth-window-moment.ts`). */
  readonly overlay = input<HdBirthWindowOverlay | undefined>(undefined);
  readonly openRequested = output<void>();

  private readonly data = inject(HdBirthWindowDataService);
  private readonly labels = inject(HdBirthWindowLabelService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);
  private readonly defaultEvaluate = createBirthWindowEvaluator(config => this.renderStateFactory.create(config));

  private readonly tiers = signal<readonly HdBirthWindowTierMargin[] | undefined>(undefined);
  protected readonly loading = signal(false);
  private requestId = 0;

  protected readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly timeZone = computed(() => {
    const birth = this.preset()?.birthData as { time?: { timezone?: { id?: string | null } | null } } | undefined;
    return birth?.time?.timezone?.id ?? undefined;
  });

  private readonly birthMs = computed(() => {
    const birth = this.preset()?.birthData as { time?: { utc?: string | null } } | undefined;
    const ms = birth?.time?.utc ? Date.parse(birth.time.utc) : Number.NaN;
    return Number.isFinite(ms) ? ms : undefined;
  });

  constructor() {
    effect(() => {
      const preset = this.preset();
      const overlay = this.overlay();
      untracked(() => void this.refresh(preset ?? undefined, overlay));
    });
  }

  private async refresh(preset: HdBodygraphChartPreset | undefined, overlay: HdBirthWindowOverlay | undefined): Promise<void> {
    const id = ++this.requestId;
    if (!preset || !preset.planetaryActivations?.length) {
      this.tiers.set(undefined);
      this.loading.set(false);
      return;
    }
    this.loading.set(true);
    try {
      const raw = await this.data.getEstimate(preset, ESTIMATE_HALF_WINDOW_MS);
      if (id !== this.requestId) return;
      if (!raw) {
        this.tiers.set(undefined);
        return;
      }
      const estimate = overlay ? toHdBirthWindowOverlayData(raw) : raw;
      const evaluate = overlay
        ? createHdBirthWindowOverlayEvaluator(overlay, config => this.renderStateFactory.create(config))
        : this.defaultEvaluate;
      const all = buildBirthWindowLanes(buildBirthWindowIntervals(estimate), estimate.birthMs, evaluate);
      const lanes = overlay ? filterHdBirthWindowOverlayLanes(all) : all;
      this.tiers.set(tierMargins(marginsFromLanes(lanes, estimate.birthMs, true), planetTierMargin(estimate, estimate.birthMs, true)));
    } catch {
      if (id === this.requestId) this.tiers.set(undefined);
    } finally {
      if (id === this.requestId) this.loading.set(false);
    }
  }

  protected readonly segments = computed<IndicatorSegmentVm[]>(() => {
    const lang = this.lang();
    const tiers = this.tiers();
    const birthMs = this.birthMs();
    const shown = this.overlay() ? HD_BIRTH_WINDOW_INDICATOR_TIERS.filter(tier => HD_BIRTH_WINDOW_OVERLAY_TIERS.includes(tier)) : HD_BIRTH_WINDOW_INDICATOR_TIERS;
    return shown.map(tier => {
      const margin = tiers?.find(candidate => candidate.tier === tier);
      const label = this.labels.tierLabel(tier, lang);
      if (!margin || birthMs === undefined) {
        return { tier, label, color: 'unknown', critical: false, text: `${label}: ${this.t('indicator.unknown', lang)}` };
      }
      return { tier, label, color: margin.color, critical: margin.critical, text: `${label}: ${this.marginText(margin, birthMs, lang)}` };
    });
  });

  protected readonly tooltip = computed(() => {
    const lang = this.lang();
    const lines = [this.t('indicator.title', lang), ...this.segments().map(segment => segment.text)];
    lines.push(this.t('indicator.open', lang));
    return lines.join('\n');
  });

  protected readonly ariaLabel = computed(() => this.tooltip().replace(/\n/g, '. '));

  private marginText(margin: HdBirthWindowTierMargin, birthMs: number, lang: string): string {
    if (margin.nearestMs === undefined) {
      return this.t('margin.noChange', lang, { hours: ESTIMATE_HALF_WINDOW_MS / 3_600_000 });
    }
    const at = margin.direction === 'before' ? birthMs - margin.nearestMs : birthMs + margin.nearestMs;
    const time = formatBirthClock(at, this.timeZone(), lang);
    const delta = this.t(margin.direction === 'before' ? 'margin.earlier' : 'margin.later', lang, {
      delta: formatBirthWindowDuration(margin.nearestMs, lang),
    });
    const key = margin.direction === 'after' ? 'margin.stableUntil' : 'margin.changesAt';
    const colorLabel = this.t(`colors.${margin.color}`, lang);
    const estimated = margin.estimated ? ` (${this.t('margin.estimated', lang)})` : '';
    return `${colorLabel}, ${this.t(key, lang, { time, delta })}${estimated}`;
  }

  private t(key: string, lang: string, params?: Record<string, unknown>): string {
    return this.transloco.translate<string>(`${BW}.${hdBirthWindowTextKey(key, this.moment())}`, params, lang);
  }
}
